// Training admin (Phase 4a) — HR course catalog, enrollment and completion.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, Users, GraduationCap, CalendarDays, UserCheck } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const blankCourse = { title: '', category: 'General', provider: '', durationHours: 1, mandatory: false, companyWide: false };
const tabBtn = (active) => ({ padding: '8px 14px', borderRadius: 10, border: `1px solid ${active ? AG.primary : AG.n200}`, background: active ? AG.primaryLightest : '#fff', color: active ? AG.primaryDark : AG.n600, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' });
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };
// UTC ISO → a <input type=datetime-local> value in the viewer's local wall-clock,
// so editing round-trips without an offset shift (slicing the raw UTC ISO would).
const toLocalInput = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d)) return ''; return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function TrainingAdmin({ showToast }) {
  const [tab, setTab] = useState('Courses');
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [roster, setRoster] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.courses().then(setRows).catch((e) => { showToast?.(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || busy) return; setBusy(true);
    try {
      if (form.id) await api.updateCourse(form.id, form); else await api.addCourse(form);
      showToast?.('Course saved'); setForm(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (c) => { try { await api.deleteCourse(c.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Training" sub="Courses, training calendar & instructors" />
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <button type="button" style={tabBtn(tab === 'Courses')} onClick={() => setTab('Courses')}>Courses</button>
        <button type="button" style={tabBtn(tab === 'Calendar')} onClick={() => setTab('Calendar')}>Calendar</button>
        <button type="button" style={tabBtn(tab === 'Instructors')} onClick={() => setTab('Instructors')}>Instructors</button>
      </div>

      {tab === 'Calendar' && <SessionsTab courses={rows} showToast={showToast} />}
      {tab === 'Instructors' && <InstructorsTab showToast={showToast} />}
      {tab !== 'Courses' ? null : (<>
      <div className="flex items-center justify-end" style={{ gap: 12, flexWrap: 'wrap' }}>
        <PrimaryBtn onClick={() => setForm({ ...blankCourse })}><Plus size={14} style={{ marginRight: 6 }} /> New course</PrimaryBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={GraduationCap} title="No courses" desc="Create a course, then enroll employees." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.title} {c.mandatory && <Pill tone="error">Mandatory</Pill>} {c.entityId ? null : <Pill tone="neutral">Company-wide</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.category}{c.provider ? ` · ${c.provider}` : ''} · {c.durationHours}h</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: c.id, title: c.title, category: c.category, provider: c.provider || '', durationHours: c.durationHours, mandatory: c.mandatory })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4, fontSize: 12, fontWeight: 600 }}>Edit</button>
                  <button onClick={() => remove(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                <span style={{ fontSize: 12, color: AG.n500 }}>{c.completed}/{c.total} completed · {c.rate}%</span>
                <div className="flex items-center gap-2">
                  <OutlineBtn onClick={() => api.courseEnrollments(c.id).then((r) => setRoster({ title: c.title, rows: r })).catch((e) => showToast?.(e.message))}><Users size={14} style={{ marginRight: 4 }} /> Roster</OutlineBtn>
                  <PrimaryBtn onClick={() => setEnrollFor({ id: c.id, title: c.title })}>Enroll</PrimaryBtn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CourseDialog form={form} setForm={setForm} onSave={save} busy={busy} />
      {enrollFor && <EnrollDialog course={enrollFor} onClose={() => setEnrollFor(null)} onDone={(n) => { showToast?.(`${n} enrolled`); setEnrollFor(null); load(); }} showToast={showToast} />}
      {roster && <RosterDialog roster={roster} onClose={() => setRoster(null)} />}
      </>)}
    </div>
  );
}

// Training calendar (3.x) — scheduled sessions with capacity + an attendance roster.
function SessionsTab({ courses, showToast }) {
  const [rows, setRows] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState(null);
  const [roster, setRoster] = useState(null);
  const load = () => api.trainingSessions().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.instructors().then(setInstructors).catch(() => {}); }, []);

  const blank = { title: '', courseId: '', instructorId: '', startsAt: '', endsAt: '', location: '', capacity: 20, notes: '' };
  const save = async () => {
    if (!form.title?.trim() || !form.startsAt) { showToast?.('Title and start time are required'); return; }
    try {
      const payload = { ...form, courseId: form.courseId || null, instructorId: form.instructorId || null, capacity: Number(form.capacity) || 0 };
      if (form.id) await api.updateTrainingSession(form.id, payload); else await api.createTrainingSession(payload);
      setForm(null); load();
    } catch (e) { showToast?.(e.message); }
  };
  const remove = async (s) => { try { await api.deleteTrainingSession(s.id); load(); } catch (e) { showToast?.(e.message); } };
  const setStatus = async (s, status) => { try { await api.updateTrainingSession(s.id, { status }); load(); } catch (e) { showToast?.(e.message); } };
  const mark = async (s, reg, status) => { try { await api.markSessionAttendance(s.id, reg.id, status); const fresh = (await api.trainingSessions()).find((x) => x.id === s.id); setRoster(fresh); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 10 }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0 }}>Schedule sessions; employees self-register up to capacity.</p>
        <PrimaryBtn onClick={() => setForm({ ...blank })}><Plus size={14} style={{ marginRight: 6 }} /> New session</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={CalendarDays} title="No sessions" desc="Schedule a training session for employees to register." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((s) => (
            <Card key={s.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: AG.text }}>{s.title} <Pill tone={s.status === 'Scheduled' ? 'info' : s.status === 'Completed' ? 'success' : 'neutral'}>{s.status}</Pill></div>
                  <div style={{ fontSize: 12.5, color: AG.n500, marginTop: 3 }}>{fmtWhen(s.startsAt)}{s.location ? ` · ${s.location}` : ''}{s.instructorName ? ` · ${s.instructorName}` : ''}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{s.registered} registered{s.capacity ? ` · ${s.seatsLeft} seat(s) left` : ' · unlimited'}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: s.id, title: s.title, courseId: s.courseId || '', instructorId: s.instructorId || '', startsAt: toLocalInput(s.startsAt), endsAt: toLocalInput(s.endsAt), location: s.location || '', capacity: s.capacity, notes: s.notes || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4, fontSize: 12, fontWeight: 600 }}>Edit</button>
                  <button onClick={() => remove(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                <OutlineBtn onClick={() => setRoster(s)}><Users size={14} style={{ marginRight: 4 }} /> Roster ({s.registered})</OutlineBtn>
                {s.status === 'Scheduled' && <OutlineBtn onClick={() => setStatus(s, 'Completed')}>Mark completed</OutlineBtn>}
                {s.status === 'Scheduled' && <OutlineBtn onClick={() => setStatus(s, 'Cancelled')}>Cancel</OutlineBtn>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit session' : 'New session'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <div className="flex gap-2">
                <TextField size="small" type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }} value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} sx={{ flex: 1 }} />
                <TextField size="small" type="datetime-local" label="Ends" InputLabelProps={{ shrink: true }} value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} sx={{ flex: 1 }} />
              </div>
              <TextField size="small" label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Room / Online" />
              <div className="flex gap-2">
                <label style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Instructor</span>
                  <select className="ag-input" value={form.instructorId} onChange={(e) => setForm({ ...form, instructorId: e.target.value })}><option value="">—</option>{instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select>
                </label>
                <label style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Course</span>
                  <select className="ag-input" value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })}><option value="">—</option>{(courses || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
                </label>
              </div>
              <TextField size="small" type="number" label="Capacity (0 = unlimited)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} inputProps={{ min: 0 }} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)}>Cancel</OutlineBtn><PrimaryBtn onClick={save}>Save</PrimaryBtn></DialogActions>
      </Dialog>

      <Dialog open={!!roster} onClose={() => setRoster(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{roster?.title} · roster</DialogTitle>
        <DialogContent>
          {roster && ((roster.registrations || []).filter((r) => r.status !== 'Cancelled').length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No registrations yet.</p> : (
            <div style={{ display: 'grid', gap: 6, paddingTop: 6 }}>
              {(roster.registrations || []).filter((r) => r.status !== 'Cancelled').map((r) => (
                <div key={r.id} className="flex items-center justify-between" style={{ padding: '8px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: AG.text }}>{r.employeeName}</span>
                  <div className="flex items-center gap-2">
                    <Pill tone={r.status === 'Attended' ? 'success' : 'neutral'}>{r.status}</Pill>
                    <button onClick={() => mark(roster, r, r.status === 'Attended' ? 'Registered' : 'Attended')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 12.5 }}>{r.status === 'Attended' ? 'Unmark' : 'Mark attended'}</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setRoster(null)}>Close</OutlineBtn></DialogActions>
      </Dialog>
    </div>
  );
}

// Instructor profiles (3.x).
function InstructorsTab({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.instructors().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const blank = { name: '', expertise: '', email: '', bio: '' };
  const save = async () => {
    if (!form.name?.trim()) { showToast?.('A name is required'); return; }
    try { if (form.id) await api.updateInstructor(form.id, form); else await api.createInstructor(form); setForm(null); load(); }
    catch (e) { showToast?.(e.message); }
  };
  const remove = async (i) => { try { await api.deleteInstructor(i.id); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end"><PrimaryBtn onClick={() => setForm({ ...blank })}><Plus size={14} style={{ marginRight: 6 }} /> New instructor</PrimaryBtn></div>
      {rows.length === 0 ? <Card><EmptyState icon={UserCheck} title="No instructors" desc="Add instructor profiles to assign them to sessions." /></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((i) => (
            <Card key={i.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: AG.text }}>{i.name}</div>
                  {i.expertise && <div style={{ fontSize: 12.5, color: AG.n500, marginTop: 2 }}>{i.expertise}</div>}
                  {i.email && <div style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>{i.email}</div>}
                  <div style={{ fontSize: 12, color: AG.n400, marginTop: 4 }}>{i.sessionCount} session(s)</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: i.id, name: i.name, expertise: i.expertise || '', email: i.email || '', bio: i.bio || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4, fontSize: 12, fontWeight: 600 }}>Edit</button>
                  <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit instructor' : 'New instructor'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField size="small" label="Expertise" value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })} placeholder="e.g. Leadership, Agile" />
              <TextField size="small" label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <TextField size="small" label="Bio" multiline minRows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)}>Cancel</OutlineBtn><PrimaryBtn onClick={save}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}

function CourseDialog({ form, setForm, onSave, busy }) {
  return (
    <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{form?.id ? 'Edit course' : 'New course'}</DialogTitle>
      <DialogContent>
        {form && (
          <div style={{ paddingTop: 6 }}>
            <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 1.5 }} />
            <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
              <TextField size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <TextField size="small" label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
            </div>
            <TextField fullWidth size="small" type="number" label="Duration (hours)" value={form.durationHours} onChange={(e) => setForm({ ...form, durationHours: e.target.value })} sx={{ mb: 1 }} />
            <FormControlLabel control={<Checkbox checked={!!form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} />} label="Mandatory" />
            {!form.id && <FormControlLabel control={<Checkbox checked={!!form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Available company-wide" />}
          </div>
        )}
      </DialogContent>
      <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={onSave} disabled={busy}>Save</PrimaryBtn></DialogActions>
    </Dialog>
  );
}

function EnrollDialog({ course, onClose, onDone, showToast }) {
  const [people, setPeople] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const enroll = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length || busy) return; setBusy(true);
    try { const r = await api.enrollEmployees(course.id, ids); onDone(r.enrolled); } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Enroll · {course.title}</DialogTitle>
      <DialogContent>
        {!people ? <CenterSpinner /> : (
          <div style={{ paddingTop: 4, maxHeight: 360, overflowY: 'auto' }}>
            {people.map((p) => (
              <FormControlLabel key={p.id} control={<Checkbox checked={!!sel[p.id]} onChange={() => toggle(p.id)} />} label={`${p.name} · ${p.dept || ''}`} sx={{ display: 'flex' }} />
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions><OutlineBtn onClick={onClose} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={enroll} disabled={busy}>Enroll</PrimaryBtn></DialogActions>
    </Dialog>
  );
}

function RosterDialog({ roster, onClose }) {
  const tone = (s) => (s === 'Completed' ? 'success' : s === 'InProgress' ? 'info' : 'warning');
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Roster · {roster.title}</DialogTitle>
      <DialogContent>
        {roster.rows.length === 0 ? <p style={{ fontSize: 13, color: AG.n500, padding: '8px 0' }}>No one enrolled yet.</p> : (
          <div style={{ display: 'grid', gap: 8, paddingTop: 4 }}>
            {roster.rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between">
                <span style={{ fontSize: 13.5, color: AG.text }}>{r.name} <span style={{ color: AG.n400 }}>· {r.dept}</span></span>
                <span className="flex items-center gap-2"><span style={{ fontSize: 12, color: AG.n500 }}>{r.progress}%</span><Pill tone={tone(r.status)}>{r.status}</Pill></span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions><OutlineBtn onClick={onClose}>Close</OutlineBtn></DialogActions>
    </Dialog>
  );
}
